import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import { useDrag, useDrop } from "react-dnd";
import { Check, ChevronLeft, ChevronRight, Loader2, Star } from "lucide-react";

import { userAPI } from "../UserAPI";
import { authenticationAtom } from "../State";
import DIN276CategoryPicker from "../components/DIN276CategoryPicker";
import { useModalContext } from "../components/ModalProvider";
import { useUrlPageState } from "../utilities/dataTablePagination";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { cn } from "@/lib/utils";

/**
 * **Produktbilder sichten** — eine Kostengruppe, alle Bilder, falsche per Ziehen in eine Rolle.
 *
 * Die Bilder sind Hypothesen der Pipeline `product-images.py` (tri-data-pipelines). Jede Entscheidung
 * hier bestätigt das Bild (`confirmed: true`); ein neuer Pipeline-Lauf lässt Bestätigtes stehen.
 * Server: `POST /products/photos/page`, `PATCH /products/:id/photos`, `POST /products/photos/confirm`.
 */

/** Die Rollen — dieselbe Liste wie `PHOTO_PURPOSES` in tri-server. */
const PURPOSES = ["product", "rejected", "wrongProduct", "drawing", "application"] as const;
type Purpose = (typeof PURPOSES)[number];

const PAGE_SIZE = 60;
const DRAG_TYPE = "PRODUCT_PHOTO";
// Wer Produktbilder pflegt: der Plattformanbieter (die Rolle gilt für alle Mandanten).
const PROVIDER_TENANT = "S8QW-G8R2-9QLC";

interface Card {
    productId: string;
    name: string;
    model?: string;
    manufacturerName?: string;
    din276Category?: string;
    url: string;
    match?: "typename" | "model";
    confirmed?: boolean;
    isMain: boolean;
}

const cardsOf = (rows: any[], purpose: Purpose, onlyUnconfirmed: boolean): Card[] =>
    rows.flatMap((row) =>
        (row.photos || [])
            .map((p: any) => (typeof p === "string" ? { url: p, purpose: "product" } : p))
            .filter((p: any) => p.purpose === purpose && (!onlyUnconfirmed || !p.confirmed))
            .map((p: any) => ({
                productId: String(row._id),
                name: row.name,
                model: row.model,
                manufacturerName: row.manufacturerName,
                din276Category: row.din276Category,
                url: p.url,
                match: p.match,
                confirmed: p.confirmed,
                isMain: row.image === p.url,
            })),
    );

function PhotoCard({ card, editable, onConfirm }: { card: Card; editable: boolean; onConfirm: (c: Card) => void }) {
    const { t } = useTranslation();
    const [{ isDragging }, drag] = useDrag(
        () => ({
            type: DRAG_TYPE,
            item: card,
            canDrag: editable,
            collect: (monitor) => ({ isDragging: monitor.isDragging() }),
        }),
        [card, editable],
    );
    return (
        <div
            ref={drag}
            className={cn(
                "group relative flex w-44 flex-col rounded-md border bg-card p-2 text-xs",
                editable && "cursor-grab",
                isDragging && "opacity-40",
                card.confirmed && "border-emerald-500/60",
            )}
            title={[card.name, card.model, card.manufacturerName].filter(Boolean).join(" · ")}
        >
            <div className="flex h-36 items-center justify-center overflow-hidden rounded bg-white">
                <img
                    src={userAPI.getResourceURL(card.url)}
                    alt={card.name}
                    loading="lazy"
                    draggable={false}
                    className="max-h-full max-w-full object-contain"
                />
            </div>
            <div className="mt-1 line-clamp-2 font-medium leading-tight">{card.name}</div>
            <div className="truncate text-muted-foreground">{card.manufacturerName || card.din276Category}</div>
            <div className="mt-1 flex items-center gap-1">
                <Badge variant="outline" className="px-1 py-0 text-[10px]">
                    {card.match === "typename"
                        ? t("settingsPanel.productImagesPanel.matchTypename")
                        : t("settingsPanel.productImagesPanel.matchModel")}
                </Badge>
                {card.isMain && <Star className="h-3 w-3 fill-amber-400 text-amber-400" />}
                {card.confirmed ? (
                    <Check className="ml-auto h-4 w-4 text-emerald-600" />
                ) : (
                    editable && (
                        <button
                            type="button"
                            className="ml-auto rounded p-0.5 text-muted-foreground opacity-0 hover:bg-accent group-hover:opacity-100"
                            onClick={() => onConfirm(card)}
                            aria-label={t("settingsPanel.productImagesPanel.confirm")}
                        >
                            <Check className="h-4 w-4" />
                        </button>
                    )
                )}
            </div>
        </div>
    );
}

function DropZone({ purpose, count, onDrop }: { purpose: Purpose; count: number; onDrop: (c: Card) => void }) {
    const { t } = useTranslation();
    const [{ isOver }, drop] = useDrop(
        () => ({
            accept: DRAG_TYPE,
            drop: (item: Card) => onDrop(item),
            collect: (monitor) => ({ isOver: monitor.isOver() }),
        }),
        [onDrop],
    );
    return (
        <div
            ref={drop}
            className={cn(
                "flex min-h-20 flex-col justify-center rounded-md border-2 border-dashed p-3 text-sm transition-colors",
                isOver ? "border-primary bg-accent" : "border-muted-foreground/30",
            )}
        >
            <div className="font-medium">{t(`settingsPanel.productImagesPanel.purpose.${purpose}`)}</div>
            <div className="text-xs text-muted-foreground">{t(`settingsPanel.productImagesPanel.purposeHint.${purpose}`)}</div>
            {count > 0 && <div className="mt-1 text-xs">{t("settingsPanel.productImagesPanel.moved", { count })}</div>}
        </div>
    );
}

export default function ProductImagesPanel() {
    const { t } = useTranslation();
    const [authentication] = useAtom(authenticationAtom) as any;
    const editable = authentication?.customer === PROVIDER_TENANT;
    const { showConfirmationDialog } = useModalContext() as any;

    const [category, setCategory] = useState<string | undefined>(undefined);
    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");
    const [purpose, setPurpose] = useState<Purpose>("product");
    const [onlyUnconfirmed, setOnlyUnconfirmed] = useState(false);
    const { page, onPageChange } = useUrlPageState("imagePage");

    const [rows, setRows] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [moved, setMoved] = useState<Record<string, number>>({});
    const loadRequestRef = useRef<number>(0);

    // Suche entprellt, damit nicht jeder Tastendruck eine Seite lädt.
    useEffect(() => {
        const handle = setTimeout(() => setSearch(searchInput), 300);
        return () => clearTimeout(handle);
    }, [searchInput]);

    const load = useCallback(async () => {
        if (!category) {
            setRows([]);
            setTotal(0);
            return;
        }
        setLoading(true);
        const requestId = ++loadRequestRef.current;
        try {
            const result = await userAPI.getProductPhotoPage({
                category,
                purpose,
                search,
                onlyUnconfirmed,
                skip: (page - 1) * PAGE_SIZE,
                limit: PAGE_SIZE,
            });
            if (loadRequestRef.current === requestId) {
                setRows(result.rows);
                setTotal(result.total);
            }
        } catch (error: any) {
            if (loadRequestRef.current === requestId) {
                toast.error(t("settingsPanel.productImagesPanel.loadFailed"));
            }
        } finally {
            if (loadRequestRef.current === requestId) {
                setLoading(false);
            }
        }
    }, [category, purpose, search, onlyUnconfirmed, page, t]);

    useEffect(() => {
        load();
    }, [load]);

    // Ein Filterwechsel beginnt wieder auf Seite 1.
    useEffect(() => {
        if (page !== 1) onPageChange(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [category, purpose, search, onlyUnconfirmed]);

    // Clamp page when total decreases (e.g. after confirming products with onlyUnconfirmed filter).
    useEffect(() => {
        if (total === 0) return;
        const maxPage = Math.max(1, Math.ceil(total / PAGE_SIZE));
        if (page > maxPage) {
            onPageChange(maxPage);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [total, page]);

    const cards = useMemo(() => cardsOf(rows, purpose, onlyUnconfirmed), [rows, purpose, onlyUnconfirmed]);
    const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    const applyToRow = (productId: string, next: { image: string | null; photos: any[] }) =>
        setRows((current) => current.map((r) => (String(r._id) === productId ? { ...r, ...next } : r)));

    const move = useCallback(
        async (card: Card, to: Purpose) => {
            if (!editable || to === purpose) return;
            try {
                const next = await userAPI.updateProductPhoto(card.productId, { url: card.url, purpose: to });
                applyToRow(card.productId, next);
                setMoved((m) => ({ ...m, [to]: (m[to] || 0) + 1 }));
            } catch {
                toast.error(t("settingsPanel.productImagesPanel.saveFailed"));
            }
        },
        [editable, purpose, t],
    );

    const confirmOne = async (card: Card) => {
        try {
            applyToRow(card.productId, await userAPI.updateProductPhoto(card.productId, { url: card.url }));
        } catch {
            toast.error(t("settingsPanel.productImagesPanel.saveFailed"));
        }
    };

    // Bestätigt alles, was sichtbar und offen ist — auch Karten, die niemand bewusst angesehen hat.
    // Deshalb erst die Rückfrage mit der Zahl und der Rolle, die damit festgeschrieben wird.
    const confirmPage = () => {
        const open = cards.filter((c) => !c.confirmed);
        if (!open.length) return;
        showConfirmationDialog(
            t("settingsPanel.productImagesPanel.confirmPageTitle"),
            t("settingsPanel.productImagesPanel.confirmPageMessage", {
                count: open.length,
                purpose: t(`settingsPanel.productImagesPanel.purpose.${purpose}`),
            }),
            async () => {
                try {
                    const { confirmed } = await userAPI.confirmProductPhotos(open.map((c) => ({ productId: c.productId, url: c.url })));
                    toast.success(t("settingsPanel.productImagesPanel.pageConfirmed", { count: confirmed }));
                    await load();
                } catch {
                    toast.error(t("settingsPanel.productImagesPanel.saveFailed"));
                }
            },
        );
    };

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-end gap-3">
                <div className="flex flex-col gap-1">
                    <Label>{t("settingsPanel.productImagesPanel.category")}</Label>
                    <DIN276CategoryPicker
                        value={category}
                        onChange={setCategory}
                        placeholder={t("settingsPanel.productManagementPanel.schemaCategoryFilterPlaceholder")}
                        width={340}
                    />
                </div>
                <div className="flex flex-col gap-1">
                    <Label htmlFor="product-image-search">{t("settingsPanel.productImagesPanel.search")}</Label>
                    <Input
                        id="product-image-search"
                        className="w-64"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        placeholder={t("settingsPanel.productImagesPanel.searchPlaceholder")}
                    />
                </div>
                <div className="flex flex-col gap-1">
                    <Label>{t("settingsPanel.productImagesPanel.show")}</Label>
                    <Select value={purpose} onValueChange={(v) => setPurpose(v as Purpose)}>
                        <SelectTrigger className="w-52">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {PURPOSES.map((p) => (
                                <SelectItem key={p} value={p}>
                                    {t(`settingsPanel.productImagesPanel.purpose.${p}`)}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <label className="flex items-center gap-2 pb-2 text-sm">
                    <Checkbox checked={onlyUnconfirmed} onCheckedChange={(v) => setOnlyUnconfirmed(v === true)} />
                    {t("settingsPanel.productImagesPanel.onlyUnconfirmed")}
                </label>
                {editable && (
                    <Button variant="outline" className="ml-auto" onClick={confirmPage} disabled={!cards.some((c) => !c.confirmed)}>
                        <Check className="mr-1 h-4 w-4" />
                        {t("settingsPanel.productImagesPanel.confirmPage")}
                    </Button>
                )}
            </div>

            {!editable && (
                <div className="text-sm text-muted-foreground">{t("settingsPanel.productImagesPanel.readOnly")}</div>
            )}

            {!category ? (
                <div className="py-16 text-center text-muted-foreground">{t("settingsPanel.productImagesPanel.pickCategory")}</div>
            ) : (
                <div className="flex gap-4">
                    <div className="min-w-0 flex-1">
                        <div className="mb-2 flex items-center gap-2 text-sm text-muted-foreground">
                            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                            {t("settingsPanel.productImagesPanel.summary", { total, count: cards.length })}
                            <div className="ml-auto flex items-center gap-1">
                                <Button variant="ghost" size="icon" aria-label={t("settingsPanel.productImagesPanel.previousPage")} disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
                                    <ChevronLeft className="h-4 w-4" />
                                </Button>
                                <span>
                                    {page} / {pages}
                                </span>
                                <Button variant="ghost" size="icon" aria-label={t("settingsPanel.productImagesPanel.nextPage")} disabled={page >= pages} onClick={() => onPageChange(page + 1)}>
                                    <ChevronRight className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                        {cards.length === 0 && !loading ? (
                            <div className="py-16 text-center text-muted-foreground">{t("settingsPanel.productImagesPanel.empty")}</div>
                        ) : (
                            <div className="flex flex-wrap gap-3">
                                {cards.map((card) => (
                                    <PhotoCard key={card.productId + card.url} card={card} editable={editable} onConfirm={confirmOne} />
                                ))}
                            </div>
                        )}
                    </div>
                    {editable && (
                        <div className="sticky top-4 flex w-56 shrink-0 flex-col gap-3 self-start">
                            <div className="text-sm font-medium">{t("settingsPanel.productImagesPanel.dropHere")}</div>
                            {PURPOSES.filter((p) => p !== purpose).map((p) => (
                                <DropZone key={p} purpose={p} count={moved[p] || 0} onDrop={(card) => move(card, p)} />
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
